<template>
  <!-- Fecha manual con revisión (replica el diseño): valor con subrayado punteado + lápiz;
       al clic se abre una cajilla flotante con la fecha de TEXTO LIBRE (dd/mm/aaaa), un motivo
       opcional y Cancelar/Guardar. Al guardar emite { value (ISO), reason }; el padre persiste
       la fecha y, si ya había un valor, registra el cambio en el historial de revisiones. -->
  <span class="drf">
    <template v-if="editing">
      <!-- Ya había valor: cajilla con motivo (se registra en el historial). -->
      <span v-if="!isEmpty" class="drf-box" @mousedown.stop>
        <input ref="date" v-model="draft" type="text" class="drf-date" placeholder="dd/mm/aaaa"
               @keydown.enter.prevent="commit" @keydown.esc="cancel">
        <input v-model="reason" type="text" class="drf-reason" :placeholder="$t('prm_summary_kd_reason_ph')"
               @keydown.enter.prevent="commit" @keydown.esc="cancel">
        <span class="drf-actions">
          <button type="button" class="drf-btn cancel" @click="cancel">{{ $t('prm_summary_kd_cancel') }}</button>
          <button type="button" class="drf-btn save" @click="commit">{{ $t('prm_summary_kd_save') }}</button>
        </span>
      </span>
      <!-- Primer valor (aún vacío): input sencillo, sin motivo ni historial. -->
      <input v-else ref="date" v-model="draft" type="text" class="drf-date simple" placeholder="dd/mm/aaaa"
             @keydown.enter.prevent="commit" @keydown.esc="cancel" @blur="blurSimple">
    </template>
    <span v-else class="drf-view" @click="edit">
      <span class="drf-text" :class="{ empty: isEmpty }">{{ isEmpty ? '—' : fmt(value) }}</span>
      <i class="fas fa-pencil drf-pencil"></i>
    </span>
  </span>
</template>

<script>
  import { fmtDate } from '../shared/format'

  export default {
    name: 'PrmSummaryDateRevisionField',

    props: {
      value: { type: String, default: '' }, // fecha ISO (YYYY-MM-DD) o vacío
    },

    data () {
      return { editing: false, draft: '', reason: '' }
    },

    computed: {
      isEmpty () {
        return this.value === '' || this.value === null || this.value === undefined
      },
    },

    methods: {
      fmt (v) { return fmtDate(v) },
      // dd/mm/aaaa -> ISO (YYYY-MM-DD); null si el texto no es una fecha válida.
      toIso (s) {
        const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec((s || '').trim())
        if (!m) return null
        const d = m[1].padStart(2, '0')
        const mo = m[2].padStart(2, '0')
        const y = m[3]
        const dt = new Date(y + '-' + mo + '-' + d + 'T00:00:00')
        if (isNaN(dt.getTime()) || dt.getMonth() + 1 !== Number(mo) || dt.getDate() !== Number(d)) return null
        return y + '-' + mo + '-' + d
      },
      edit () {
        this.draft = this.isEmpty ? '' : fmtDate(this.value) // se edita en dd/mm/aaaa
        this.reason = ''
        this.editing = true
        this.$nextTick(() => { if (this.$refs.date) this.$refs.date.focus() })
      },
      cancel () {
        this.editing = false
        this.reason = ''
      },
      // Input sencillo (primer valor): al perder el foco guarda si es válido o vacío; si el
      // texto es una fecha inválida, se descarta para no quedar abierto sin foco.
      blurSimple () {
        const raw = (this.draft || '').trim()
        if (raw !== '' && this.toIso(raw) === null) { this.cancel(); return }
        this.commit()
      },
      commit () {
        const raw = (this.draft || '').trim()
        let value = null
        if (raw !== '') {
          value = this.toIso(raw)
          if (value === null) return // fecha inválida: no cerramos, el usuario corrige
        }
        const cur = this.isEmpty ? null : this.value
        if (value === cur) { this.cancel(); return } // sin cambios
        this.$emit('save', { value, reason: (this.reason || '').trim() })
        this.editing = false
        this.reason = ''
      },
    },
  }
</script>

<style lang="scss" scoped>
  // El reset global (reset.scss) aplica `transition: all .3s` a inputs/botones/spans; lo
  // anulamos aquí para que la cajilla NO tenga animación al aparecer ni al cerrarse.
  .drf, .drf * { transition: none !important; }

  .drf { display: inline-flex; align-items: center; min-width: 0; max-width: 100%; }

  // Vista (solo lectura): valor con subrayado punteado ámbar + lápiz, como el diseño.
  .drf-view { display: inline-flex; align-items: center; gap: 4px; cursor: pointer; }
  .drf-text { border-bottom: .8px dashed #b7791f; color: #2c3a4d; line-height: 1.4; }
  .drf-text.empty { color: #9aa4b2; }
  .drf-pencil { font-size: 10px; color: #b7791f; flex-shrink: 0; }

  // Cajilla flotante (vertical): borde y fondo ámbar, dos inputs de texto y acciones a la derecha.
  .drf-box {
    display: inline-flex; flex-direction: column; gap: 4px; min-width: 230px;
    border: .8px solid #e0b52a; border-radius: 5px; background: #fffdf4; padding: 4px;
  }
  .drf-date, .drf-reason {
    border: .8px solid #e0b52a; border-radius: 4px; padding: 2px 6px; outline: none; background: #fff;
    font-size: 11px; color: #2c3a4d;
  }
  .drf-reason { border-color: #e6d9a9; font-size: 10px; color: #5f5130; }
  // Input sencillo (primer valor): sin cajilla, un campo normal.
  .drf-date.simple { min-width: 110px; }
  .drf-actions { display: flex; align-items: center; justify-content: flex-end; gap: 4px; }
  .drf-btn { border: 0; border-radius: 4px; padding: 2px 8px; font-size: 10px; cursor: pointer; }
  .drf-btn.cancel { background: transparent; color: #475569; }
  .drf-btn.cancel:hover { background: #f1f5f9; }
  .drf-btn.save { background: #3d6b9a; color: #fff; font-weight: 600; }
  .drf-btn.save:hover { background: #35608a; }
</style>
